import BaseEntityNew from 'client/components/core/BaseEntity/BaseEntityNew'
import ChildrenEntities from 'client/components/core/ChildrenEntities'
import { usePage, useWebsiteLogin } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import React, { createContext, useContext } from 'react'
import { selectors } from 'src/editor/store/website-login/website-login-reducer'
import { WebsiteLoginModeEnum } from 'src/editor/store/website-login/website-login-types'
import {
  generateHtmlAttrId,
  generateId,
} from 'src/shared/entities/utils/generate-base-entity'
import { WebsiteLoginInterface } from 'src/shared/types/entities/website-login-interface'
import { WebsiteLoginContainerUi } from './ui/website-login-container-ui'

type WebsiteLoginProps = {
  entity: WebsiteLoginInterface
  isMoveDownAllowed: boolean
  isMoveUpAllowed: boolean
}

const WebsiteLoginDescendantIdsContext = createContext([] as string[])

type CustomLoginDescendantIdsProviderProps = {
  children: React.ReactElement
  entityId: string
}

export const useWebsiteLoginDescendantIds = () =>
  useContext(WebsiteLoginDescendantIdsContext)

function WebsiteLoginDescendantIdsProvider({
  children,
  entityId,
}: CustomLoginDescendantIdsProviderProps) {
  const descendantIds = usePage(page =>
    pageSelectors.getDescendantIds(page, entityId),
  ).concat([entityId])
  return (
    <WebsiteLoginDescendantIdsContext.Provider value={descendantIds}>
      {children}
    </WebsiteLoginDescendantIdsContext.Provider>
  )
}

export const WebsiteLogin = ({ entity }: WebsiteLoginProps) => {
  const mode = useWebsiteLogin(selectors.getWebsiteLoginMode)
  let activeChildId: string
  switch (mode) {
    case WebsiteLoginModeEnum.Login:
      activeChildId = entity.childIds[0]
      break

    case WebsiteLoginModeEnum.Registration:
      activeChildId = entity.childIds[1]
      break
    case WebsiteLoginModeEnum.ConfirmRegistration:
      activeChildId = entity.childIds[2]
      break
    case WebsiteLoginModeEnum.Request:
      activeChildId = entity.childIds[3]
      break
    case WebsiteLoginModeEnum.Reset:
      activeChildId = entity.childIds[4]
      break
    case WebsiteLoginModeEnum.TwoFactorAuth:
      activeChildId = entity.childIds[5]
      break
    default:
      activeChildId = entity.childIds[0]
  }
  return (
    <BaseEntityNew
      entity={entity}
      isMoveUpAllowed={false}
      isMoveDownAllowed={false}
      isSaveAllowed={false}
      isRemoveAllowed={false}
      isCopyAllowed={false}
      isMoveAllowed={false}
    >
      <WebsiteLoginContainerUi
        padding={entity.padding}
        mobilePadding={entity.mobilePadding}
      >
        <WebsiteLoginDescendantIdsProvider entityId={entity.id}>
          <ChildrenEntities
            id={entity.id}
            type={entity.type}
            childIds={[activeChildId]}
          />
        </WebsiteLoginDescendantIdsProvider>
      </WebsiteLoginContainerUi>
    </BaseEntityNew>
  )
}

export function createWebsiteLogin(parentId: string) {
  return {
    id: generateId(),
    type: EntityTypeEnum.WebsiteLogin,
    parentId: parentId,
    margin: {
      marginTop: 100,
      marginRight: 100,
      marginBottom: 0,
      marginLeft: 100,
    },
    mobileMargin: {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    },
    padding: {
      paddingTop: 10,
      paddingRight: 10,
      paddingBottom: 10,
      paddingLeft: 10,
    },
    mobilePadding: {
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 5,
    },
    appearance: {
      desktop: true,
      mobile: true,
    },
    htmlAttrId: generateHtmlAttrId(EntityTypeEnum.WebsiteLogin),
    childIds: [],
  }
}
